import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLinkedThreads } from '../../../contexts/LinkedThreadsContext';
import {
  SpecialMessageArtifact,
  generateColorVariants,
  IN_MESSAGE_ARTIFACT_TYPES,
} from './threadUtils';
import {
  GetAgentThreadsResponse,
  GetThreadDetailsResponse,
  GetAuthorizationLinkResponse,
  GoogleFlightsBookingLinkResponse,
} from '@dreamai/types';
import { useAgent } from '../../../contexts/AgentContext';
import { ReactComponent as ArrowUpLinkIcon } from '../../../icons/arrow-up-link.svg';
import { ReactComponent as SideChatIcon } from '../../../icons/side-chat.svg';
import CreateCampaignModal from '../ExpansiveSideNavigation/CreateCampaignModal';

import api from '../../../api';
import './in-message-artifacts.scss';

interface InMessageArtifactsProps {
  specialMessageArtifacts: SpecialMessageArtifact[];
  threadId: string;
}

interface ThreadInfo {
  id: string;
  description?: string;
  agentId?: string;
}

interface ArtifactButtonProps {
  onClick: () => void;
  disabled?: boolean;
  colorVariants: { base: string; darker: string; lighter: string };
  label?: string;
  name: string;
  actionText: string;
  linkStyle?: boolean;
}

const ArtifactButton: React.FC<ArtifactButtonProps> = ({
  onClick,
  disabled = false,
  colorVariants,
  label,
  name,
  actionText,
  linkStyle = false,
}) => (
  <button
    className='special-artifact-button'
    onClick={onClick}
    disabled={disabled}
    style={{
      backgroundColor: colorVariants.lighter,
      borderColor: colorVariants.base,
    }}
  >
    <div className='artifact-left'>
      {!linkStyle && <SideChatIcon />}
      {label && <span className='thread-link-label'>{label}:</span>}
      <span className={`thread-link-name ${linkStyle && 'link-style'}`}>
        {name}
        {linkStyle && <ArrowUpLinkIcon />}
      </span>
    </div>
    <div className='artifact-right'>{actionText}</div>
  </button>
);

const InMessageArtifacts: React.FC<InMessageArtifactsProps> = ({
  specialMessageArtifacts,
  threadId,
}) => {
  // Filter to only in-message artifact types (exclude media and below-message types)
  const filteredArtifacts = specialMessageArtifacts.filter((artifact) =>
    (IN_MESSAGE_ARTIFACT_TYPES as readonly string[]).includes(artifact.type),
  );

  // If no artifacts remain after filtering, don't render anything
  if (filteredArtifacts.length === 0) {
    return null;
  }
  const { getAccessTokenSilently } = useAuth0();
  const { openThread } = useLinkedThreads();
  const { selectedAgent } = useAgent();
  const [threadInfos, setThreadInfos] = useState<Record<string, ThreadInfo>>(
    {},
  );
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [authorizationUrls, setAuthorizationUrls] = useState<
    Record<string, string>
  >({});
  const [authorizationErrors, setAuthorizationErrors] = useState<
    Record<string, boolean>
  >({});
  const [bookingData, setBookingData] = useState<
    Record<string, GoogleFlightsBookingLinkResponse>
  >({});
  const [bookingErrors, setBookingErrors] = useState<Record<string, boolean>>(
    {},
  );
  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [campaignContextId, setCampaignContextId] = useState<
    string | undefined
  >();

  // Fetch thread information for each linkToOtherThread artifact
  useEffect(() => {
    const fetchThreadInfo = async (targetThreadId: string) => {
      if (threadInfos[targetThreadId] || loading[targetThreadId]) return;

      setLoading((prev) => ({ ...prev, [targetThreadId]: true }));

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<GetThreadDetailsResponse>(
          `/agents/threads/${targetThreadId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data.success && response.data.thread) {
          const thread = response.data.thread;
          setThreadInfos((prev) => ({
            ...prev,
            [targetThreadId]: {
              id: thread.id,
              description: thread.description || 'Thread',
              agentId: thread.agent_id,
            },
          }));
        } else {
          // Response failed or thread not found - set fallback to prevent infinite retries
          setThreadInfos((prev) => ({
            ...prev,
            [targetThreadId]: { id: targetThreadId, description: 'Thread' },
          }));
        }
      } catch (error) {
        console.error('Error fetching thread info:', error);
        // Set a fallback thread info
        setThreadInfos((prev) => ({
          ...prev,
          [targetThreadId]: { id: targetThreadId, description: 'Thread' },
        }));
      } finally {
        setLoading((prev) => ({ ...prev, [targetThreadId]: false }));
      }
    };

    // Fetch info for all linkToOtherThread artifacts
    filteredArtifacts.forEach((artifact) => {
      if (artifact.type === 'linkToOtherThread' && artifact.data.threadId) {
        fetchThreadInfo(artifact.data.threadId);
      }
    });
  }, [filteredArtifacts, getAccessTokenSilently]);

  // Fetch authorization URLs for all authorizationLink artifacts
  useEffect(() => {
    const fetchAuthorizationUrl = async (authorizationKey: string) => {
      if (
        authorizationUrls[authorizationKey] ||
        authorizationErrors[authorizationKey]
      )
        return;

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<GetAuthorizationLinkResponse>(
          `/authorization-links/${authorizationKey}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data.authorizationUrl) {
          setAuthorizationUrls((prev) => ({
            ...prev,
            [authorizationKey]: response.data.authorizationUrl,
          }));
        }
      } catch (error) {
        console.error('Error fetching authorization URL:', error);
        setAuthorizationErrors((prev) => ({
          ...prev,
          [authorizationKey]: true,
        }));
      }
    };

    // Fetch URLs for all authorizationLink artifacts
    filteredArtifacts.forEach((artifact) => {
      if (
        artifact.type === 'authorizationLink' &&
        artifact.data.authorizationKey
      ) {
        fetchAuthorizationUrl(artifact.data.authorizationKey);
      }
    });
  }, [
    filteredArtifacts,
    getAccessTokenSilently,
    authorizationUrls,
    authorizationErrors,
  ]);

  // Fetch booking data for all googleFlightsLink artifacts
  useEffect(() => {
    const fetchBookingData = async (bookingKey: string) => {
      if (bookingData[bookingKey] || bookingErrors[bookingKey]) return;

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<GoogleFlightsBookingLinkResponse>(
          `/google-flights-booking-links/${bookingKey}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data.bookingUrl && response.data.postData) {
          setBookingData((prev) => ({
            ...prev,
            [bookingKey]: response.data,
          }));
        }
      } catch (error) {
        console.error('Error fetching booking data:', error);
        setBookingErrors((prev) => ({
          ...prev,
          [bookingKey]: true,
        }));
      }
    };

    // Fetch data for all googleFlightsLink artifacts
    filteredArtifacts.forEach((artifact) => {
      if (artifact.type === 'googleFlightsLink' && artifact.data.bookingKey) {
        fetchBookingData(artifact.data.bookingKey);
      }
    });
  }, [filteredArtifacts, getAccessTokenSilently, bookingData, bookingErrors]);

  const handleThreadClick = (artifact: SpecialMessageArtifact) => {
    if (artifact.type === 'linkToOtherThread') {
      const { threadId: targetThreadId, agentId: artifactAgentId } =
        artifact.data;

      if (!targetThreadId) return;

      // Use agentId from artifact data, or fall back to fetched thread info
      const threadInfo = threadInfos[targetThreadId];
      const agentId = artifactAgentId || threadInfo?.agentId;

      if (!agentId) return;

      // Use the LinkedThreadsContext to open the thread in a mini view
      // We'll use a special component name to indicate this is a linked thread
      openThread(
        targetThreadId,
        'LinkedThread',
        { threadId: targetThreadId }, // Simple match object
        agentId,
      );
    }
  };

  const handleAuthorizationClick = (artifact: SpecialMessageArtifact) => {
    if (artifact.type === 'authorizationLink') {
      const { authorizationKey } = artifact.data;

      if (!authorizationKey) return;

      const url = authorizationUrls[authorizationKey];
      if (url) {
        window.open(url, '_blank');
      }
    }
  };

  const handleBookingClick = (artifact: SpecialMessageArtifact) => {
    if (artifact.type === 'googleFlightsLink') {
      const { bookingKey } = artifact.data;

      if (!bookingKey) return;

      const booking = bookingData[bookingKey];
      if (booking) {
        // Create a form that submits the URL-encoded POST data
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = booking.bookingUrl;
        form.target = '_blank';
        form.style.display = 'none';
        form.enctype = 'application/x-www-form-urlencoded';

        // Parse the URL-encoded POST data and create form fields
        const params = new URLSearchParams(booking.postData);
        params.forEach((value, key) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value;
          form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
      }
    }
  };

  const handleProDocClick = (artifact: SpecialMessageArtifact) => {
    if (artifact.type === 'updateToProDoc') {
      const { agentId } = artifact.data;

      if (!agentId) return;

      // Navigate to the main app with the agent ID
      window.location.href = `/?agent_id=${agentId}`;
    }
  };

  const handleCampaignCreationClick = (artifact: SpecialMessageArtifact) => {
    if (artifact.type === 'campaignCreationLink') {
      const { contextId } = artifact.data;
      setCampaignContextId(contextId);
      setCampaignModalOpen(true);
    }
  };

  // Generate color variants from agent's predominant color
  const colorVariants = selectedAgent?.predominantColour
    ? generateColorVariants(selectedAgent.predominantColour)
    : { base: '#97D5FC', darker: '#4A90C2', lighter: '#E3F2FD' };

  return (
    <div className='special-message-artifacts'>
      {filteredArtifacts.map((artifact, index) => {
        if (artifact.type === 'linkToOtherThread') {
          const { threadId: targetThreadId } = artifact.data;

          if (!targetThreadId) return null;

          const threadInfo = threadInfos[targetThreadId];
          const isLoading = loading[targetThreadId];

          return (
            <ArtifactButton
              key={index}
              onClick={() => handleThreadClick(artifact)}
              disabled={isLoading}
              colorVariants={colorVariants}
              label='Chat'
              name={isLoading ? '' : threadInfo?.description || 'Thread'}
              actionText='Show'
            />
          );
        } else if (artifact.type === 'authorizationLink') {
          const { authorizationKey } = artifact.data;

          if (!authorizationKey || authorizationErrors[authorizationKey])
            return null;

          return (
            <ArtifactButton
              key={index}
              onClick={() => handleAuthorizationClick(artifact)}
              colorVariants={colorVariants}
              name='Complete Account Authorisation'
              actionText='Open'
              linkStyle
            />
          );
        } else if (artifact.type === 'googleFlightsLink') {
          const { bookingKey } = artifact.data;

          if (!bookingKey || bookingErrors[bookingKey]) return null;

          const booking = bookingData[bookingKey];
          const isLoading = !booking && !bookingErrors[bookingKey];

          // Use providerName from artifact data, fallback to bookWith from API response, then default
          const displayName = booking?.bookWith || 'Book Flight';
          const optionTitle = booking?.optionTitle || 'Option';

          return (
            <ArtifactButton
              key={index}
              onClick={() => handleBookingClick(artifact)}
              disabled={isLoading || !booking}
              colorVariants={colorVariants}
              name={`Book with ${displayName} - ${optionTitle}`}
              actionText='Book'
              linkStyle
            />
          );
        } else if (artifact.type === 'updateToProDoc') {
          const { agentId, shortSummary, docDescriptor } = artifact.data;

          if (!agentId || !shortSummary) return null;

          return (
            <ArtifactButton
              key={index}
              onClick={() => handleProDocClick(artifact)}
              colorVariants={colorVariants}
              name='View document'
              actionText='View'
            />
          );
        } else if (artifact.type === 'campaignCreationLink') {
          const { contextId } = artifact.data;

          if (!contextId) return null;

          return (
            <ArtifactButton
              key={index}
              onClick={() => handleCampaignCreationClick(artifact)}
              colorVariants={colorVariants}
              name='Create new campaign'
              actionText='Start'
              linkStyle
            />
          );
        }
        return null;
      })}

      <CreateCampaignModal
        show={campaignModalOpen}
        onClose={() => setCampaignModalOpen(false)}
        contextId={campaignContextId}
        agentName={selectedAgent?.label}
      />
    </div>
  );
};

export default InMessageArtifacts;
